/**
 * Étape 2 — Identification de l'exploitation par SIRET.
 *
 * Appel `/api/diagnostic/siret` pour récupérer les infos INSEE
 * (raison sociale, code NAF, segment). Pré-remplit la suite du funnel.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, BadgeCheck, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import { apiFetch, ApiError } from '../../../lib/api';
import { easeStripe } from '../../../lib/motion';
import { agriStepCounterLabel, useAgriStore } from './useAgriStore';

function formatSiret(digits: string): string {
  const parts = [
    digits.slice(0, 3),
    digits.slice(3, 6),
    digits.slice(6, 9),
    digits.slice(9, 14),
  ];
  return parts.filter(Boolean).join(' ');
}

interface SiretResponse {
  siret: string;
  name: string;
  naf: string | null;
  segment: string;
}

export function Step2Siret() {
  const company = useAgriStore((s) => s.company);
  const setCompany = useAgriStore((s) => s.setCompany);
  const goNext = useAgriStore((s) => s.goNext);
  const counter = useAgriStore(agriStepCounterLabel);

  const [raw, setRaw] = useState(company?.siret ?? '');
  const digits = raw.replace(/\D/g, '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (digits.length !== 14) {
      setError('Le SIRET doit comporter exactement 14 chiffres.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const data = await apiFetch<SiretResponse>('/api/diagnostic/siret', {
        json: { siret: digits },
      });
      setCompany({
        siret: data.siret,
        name: data.name,
        naf: data.naf,
        segment: data.segment,
      });
      goNext();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Impossible de trouver ce SIRET. Vérifiez le numéro et réessayez.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-400">
        {counter}
      </p>
      <h2 className="mt-2 h-section">
        Identifions votre exploitation
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Avec votre SIRET, on récupère votre raison sociale et votre code NAF
        sur le registre officiel. On vérifie ensuite votre éligibilité aux
        fiches CEE agricoles (secteur AGRI).
      </p>

      {error && (
        <Alert variant="error" className="mt-4">
          <AlertDescription className="text-[13px]">{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <Label htmlFor="agri-siret" className="text-[13px] font-semibold">
            Numéro SIRET de l'exploitation
          </Label>
          <div className="relative mt-1.5">
            <Input
              id="agri-siret"
              ref={inputRef}
              inputMode="numeric"
              maxLength={19}
              required
              autoComplete="off"
              value={formatSiret(digits)}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '');
                if (next.length <= 14) setRaw(next);
                if (error) setError(null);
              }}
              placeholder="000 000 000 00000"
              className="h-12 rounded-xl border-2 border-stone-200 pr-14 font-mono text-lg tracking-widest focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
            />
            {digits.length > 0 && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-semibold tabular-nums text-stone-400">
                {digits.length}/14
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-stone-400">
            <ShieldCheck
              className="h-3 w-3 shrink-0 text-brand-500"
              strokeWidth={2}
            />
            Via le registre officiel SIRENE — aucune donnée stockée hors UE
          </div>
        </div>

        {/* Confirmation INSEE si déjà récupérée */}
        <AnimatePresence mode="wait">
          {company && (
            <motion.div
              key={company.siret}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25, ease: easeStripe }}
              className="flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50/60 p-3.5"
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-brand-700 ring-1 ring-inset ring-brand-200/60">
                <BadgeCheck className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold text-brand-900">
                  {company.name}
                </p>
                <p className="mt-0.5 text-[11.5px] text-brand-700/80">
                  SIRET {formatSiret(company.siret)}
                  {company.naf && ` · NAF ${company.naf}`}
                  {company.segment === 'agri' && (
                    <span className="ml-2 rounded-md bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-800">
                      Secteur AGRI confirmé
                    </span>
                  )}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          type="submit"
          variant="brand"
          size="xl"
          disabled={loading || digits.length !== 14}
          className="w-full gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Vérification en cours…
            </>
          ) : (
            <>
              {company ? 'Confirmer et continuer' : 'Identifier mon exploitation'}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      <p className="mt-4 text-center text-[11px] text-stone-400">
        Pas encore de SIRET&nbsp;?{' '}
        <a
          href="https://www.formalites.entreprises.gouv.fr/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-brand-700 hover:underline"
        >
          Démarches sur formalites.entreprises.gouv.fr →
        </a>
      </p>
    </div>
  );
}
